import { useState, useRef, useEffect } from "react";
import Input from "../../UI/Input";
import RELATIVE_DATE_VALUES from "../../../constants/relativeDateValues";
import { DateFilterValue, LiteralDateFilterValue, RelativeDateValue } from "../../../models/pageModels/GroupFilters";
import "./DashboardDateFilter.css";

interface DashboardDateFilterProps {
    id: string;
    label: string;
    value: DateFilterValue | null;
    onChange: (value: DateFilterValue | null) => void;
    disabled?: boolean;
}

const RELATIVE_OPTIONS: { label: string; value: RelativeDateValue; description: string }[] = [
    { label: "Today", value: RELATIVE_DATE_VALUES.TODAY, description: "Current day" },
    { label: "MTD", value: RELATIVE_DATE_VALUES.MTD, description: "Month to date" },
    { label: "YTD", value: RELATIVE_DATE_VALUES.YTD, description: "Year to date" },
];

const getDisplayText = (value: DateFilterValue | null): string => {
    if (!value) return "";
    if ("mode" in value) {
        const opt = RELATIVE_OPTIONS.find(o => o.value === value.value);
        return opt ? opt.label : value.value;
    }
    const { from, to } = value;
    if (from && to) return `${from} - ${to}`;
    if (from) return `From ${from}`;
    if (to) return `To ${to}`;
    return "";
};

const DashboardDateFilter = ({ id, label, value, onChange, disabled = false }: DashboardDateFilterProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const isRelative = value !== null && "mode" in value;
    const activeRelative = isRelative
        ? (value as { mode: "RELATIVE"; value: RelativeDateValue }).value
        : null;
    const literalValue: LiteralDateFilterValue =
        !isRelative && value !== null && "from" in value
            ? (value as LiteralDateFilterValue)
            : { from: "", to: "" };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (disabled) {
            setIsOpen(false);
        }
    }, [disabled]);

    const handleRelativeClick = (relValue: RelativeDateValue) => {
        if (disabled) return;

        // clicking the active chip deselects it
        if (activeRelative === relValue) {
            onChange(null);
        } else {
            onChange({ mode: "RELATIVE", value: relValue });
        }
    };

    const handleFromChange = (from: string) => {
        if (disabled) return;
        onChange({ from, to: literalValue.to });
    };

    const handleToChange = (to: string) => {
        if (disabled) return;
        onChange({ from: literalValue.from, to });
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        onChange(null);
    };

    const displayText = getDisplayText(value);
    const hasValue = value !== null;

    return (
        <div className="dashboard-date-filter" ref={containerRef}>
            <label className="dashboard-date-filter-label">{label}</label>

            {/* Trigger */}
            <div
                className={`dashboard-date-filter-trigger${isOpen ? " open" : ""}${hasValue ? " has-value" : ""}${disabled ? " disabled" : ""}`}
                onClick={() => { if (!disabled) setIsOpen(prev => !prev); }}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onKeyDown={e => { if (!disabled && (e.key === "Enter" || e.key === " ")) setIsOpen(prev => !prev); }}
                id={id}
            >
                <span className={`dashboard-date-filter-display${!displayText ? " placeholder" : ""}`}>
                    {displayText || "Select date range"}
                </span>
                <div className="dashboard-date-filter-trigger-actions">
                    {hasValue && (
                        <span
                            className="ri-close-line dashboard-date-filter-clear"
                            onClick={handleClear}
                            title="Clear"
                        />
                    )}
                    {!hasValue && <span className="ri-calendar-line dashboard-date-filter-arrow" />}
                {hasValue && <span className={`ri-arrow-${isOpen ? "up" : "down"}-s-line dashboard-date-filter-arrow`} />}
                </div>
            </div>

            {/* Popover */}
            {isOpen && (
                <div className="dashboard-date-filter-popover">
                    {/* Relative chips */}
                    <p className="dashboard-date-filter-section-label">Quick select</p>
                    <div className="dashboard-date-filter-chips">
                        {RELATIVE_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`dashboard-date-filter-chip${activeRelative === opt.value ? " active" : ""}`}
                                onClick={() => handleRelativeClick(opt.value)}
                                title={opt.description}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {isRelative && (
                        <p className="dashboard-date-filter-hint">
                            Dates resolve fresh on every report run.
                        </p>
                    )}

                    <div className="dashboard-date-filter-divider">
                        <span>or pick dates manually</span>
                    </div>

                    {/* Manual date inputs */}
                    <div className={`dashboard-date-filter-manual${isRelative || disabled ? " disabled" : ""}`}>
                        <Input
                            id={`${id}-from`}
                            label="From"
                            type="date"
                            value={isRelative ? "" : literalValue.from}
                            onChange={isRelative ? undefined : handleFromChange}
                            disabled={isRelative || disabled}
                        />
                        <Input
                            id={`${id}-to`}
                            label="To"
                            type="date"
                            value={isRelative ? "" : literalValue.to}
                            onChange={isRelative ? undefined : handleToChange}
                            disabled={isRelative || disabled}
                        />
                    </div>

                    {!isRelative && (literalValue.from || literalValue.to) && !(literalValue.from && literalValue.to) && (
                        <p className="dashboard-date-filter-hint warning">
                            Both From and To are required to apply a date range.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default DashboardDateFilter;
